import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  VStack,
  Text,
  HStack,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import chatStateManager from "../services/ChatStateManager";

const ConversationsList = ({ currentPlanId, chatbotRef, onClose }) => {
  const navigate = useNavigate();
  const [savedConversations, setSavedConversations] = useState([]);
  const [hoveredConversationId, setHoveredConversationId] = useState(null);
  const [isConversationAlertOpen, setIsConversationAlertOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const cancelRef = useRef();
  const [activeConversationId, setActiveConversationId] = useState(null);
  const previousPlanIdRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  const fetchConversations = useCallback(async () => {
    if (!currentPlanId) {
      setSavedConversations([]);
      return;
    }

    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    try {
      console.log("ConversationsList: Fetching for plan", currentPlanId);
      const response = await fetchWithAuth("/api/get-conversations");
      const conversations = await response.json();

      const planConversations = (Array.isArray(conversations) ? conversations : []).filter(
        (conv) =>
          conv.plan_id === parseInt(currentPlanId) ||
          conv.plan_id === currentPlanId,
      );

      console.log("ConversationsList: Found conversations for plan", currentPlanId, planConversations.length);
      setSavedConversations(planConversations);

      const cacheKey = `conversations_plan_${currentPlanId}`;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(planConversations));
        localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
      } catch (cacheError) {
        console.warn("Unable to cache conversation metadata:", cacheError);
      }
    } catch (error) {
      console.error("Error fetching saved conversations:", error);
      setSavedConversations([]);
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [currentPlanId]);

  useEffect(() => {
    const normalizedPlanId = currentPlanId ? String(currentPlanId) : null;
    const normalizedPrevId = previousPlanIdRef.current ? String(previousPlanIdRef.current) : null;
    
    if (normalizedPlanId && normalizedPlanId !== normalizedPrevId) {
      previousPlanIdRef.current = currentPlanId;
      fetchConversations();
    }

    const unsubscribe = chatStateManager.subscribe((event) => {
      if (
        event.type === "conversation_saved" ||
        event.type === "conversation_created" ||
        event.type === "conversation_plan_updated"
      ) {
        fetchConversations();
      }
    });

    return unsubscribe;
  }, [currentPlanId, fetchConversations]);

  const handleConversationClick = async (conversation) => {
    try {
      console.log("🔥🔥 USER CLICKED CONVERSATION in ConversationsList:", {
        conversationId: conversation.id,
        planId: conversation.plan_id,
        hasTitle: !!conversation.title,
        action: "LOAD_INTO_EMBEDDED_CHAT",
      });
      console.log(
        "🔥 STEP 1: ConversationsList.handleConversationClick START",
        conversation.id,
      );
      // Set active conversation immediately to provide visual feedback
      setActiveConversationId(conversation.id);

      console.log(
        "ConversationsList: Navigating to conversation",
        conversation.id,
        "with plan_id:",
        conversation.plan_id,
      );

      // Navigation logic based on plan_id
      if (conversation.plan_id) {
        // Scenario A: Has project linked → navigate to Plan page WITH conversation loading state
        console.log(
          "🔥 ConversationsList: Navigating to Plan page for plan",
          conversation.plan_id,
          "with conversation",
          conversation.id,
        );
        const navigationState = {
          loadConversation: true,
          conversationId: conversation.id,
        };
        console.log("🔥 ConversationsList: Navigation state:", navigationState);
        navigate(`/plan/${conversation.plan_id}`, {
          state: navigationState,
        });
      } else {
        // Scenario B: Generic conversation → navigate to Brief page via /c/ URL
        console.log(
          "ConversationsList: Navigating to Brief page for conversation",
          conversation.id,
        );
        navigate(`/c/${conversation.id}`);
      }

      console.log(
        "🔥 STEP 2: ConversationsList calling chatStateManager.loadConversation",
      );
      // Load conversation in ChatStateManager
      await chatStateManager.loadConversation(conversation.id);
      console.log(
        "🔥 STEP 3: ConversationsList chatStateManager.loadConversation COMPLETED",
      );

      // Close the popover after navigation and auto-collapse left nav
      if (onClose) {
        console.log(
          "🔥 ConversationsList: Calling onClose to auto-collapse left navigation",
        );
        onClose();
      }
      console.log("🔥 STEP 4: ConversationsList.handleConversationClick END");
    } catch (error) {
      console.error("Error navigating to conversation:", error);
    }
  };

  const openConversationAlert = (id) => {
    // Prevent deleting the currently active conversation
    if (id === activeConversationId) {
      return; // Do nothing if trying to delete active conversation
    }
    setConversationToDelete(id);
    setIsConversationAlertOpen(true);
  };

  const closeConversationAlert = () => {
    setIsConversationAlertOpen(false);
    setConversationToDelete(null);
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      const response = await fetchWithAuth(
        `/api/delete-conversation/${conversationToDelete}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        setSavedConversations((prev) =>
          prev.filter((c) => c.id !== conversationToDelete),
        );
        closeConversationAlert();
      } else {
        console.error("Failed to delete conversation");
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  if (savedConversations.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        No saved conversations for this plan
      </Text>
    );
  }

  return (
    <>
      <VStack align="start" spacing={1} width="100%">
        <Text fontWeight="bold" mb={2} fontSize="sm">
          Chat history
        </Text>
        {savedConversations.map((conv) => (
          <HStack
            key={conv.id}
            justify="space-between"
            width="100%"
            height="42px"
            pl={2}
            pr={2}
            borderRadius="md"
            fontSize="sm"
            backgroundColor={
              activeConversationId === conv.id ? "brand.100" : "white"
            }
            borderLeft={
              activeConversationId === conv.id
                ? "3px solid"
                : "3px solid transparent"
            }
            borderColor={
              activeConversationId === conv.id ? "brand.500" : "transparent"
            }
            _hover={{
              backgroundColor:
                activeConversationId === conv.id ? "brand.300" : "gray.100",
            }}
            cursor="pointer"
            onMouseEnter={() => setHoveredConversationId(conv.id)}
            onMouseLeave={() => setHoveredConversationId(null)}
            onClick={() => handleConversationClick(conv)}
          >
            <Text paddingRight="32px" isTruncated>
              {conv.title || `Conversation #${conv.id}`}
            </Text>
            {hoveredConversationId === conv.id &&
              conv.id !== activeConversationId && (
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      icon={
                        <span className="material-icons-outlined">
                          more_horiz
                        </span>
                      }
                      variant="ghost"
                      size="xs"
                      aria-label="More actions"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </PopoverTrigger>
                  <PopoverContent width="120px">
                    <PopoverArrow />
                    <PopoverBody>
                      <Button
                        variant="ghost"
                        width="100%"
                        size="sm"
                        justifyContent="start"
                        leftIcon={
                          <span className="material-icons-outlined">
                            delete
                          </span>
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          openConversationAlert(conv.id);
                        }}
                      >
                        Delete
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
          </HStack>
        ))}
      </VStack>

      {/* Conversation Deletion Alert */}
      <AlertDialog
        isOpen={isConversationAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeConversationAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Conversation
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this conversation? This action
              cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeConversationAlert}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConversation}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ConversationsList;
